/**
 * Port of `repos/opencode/packages/session-ui/src/v2/components/tool-error-card-v2.tsx`,
 * keyed on `styles/components/tool-error-card-v2.css` (vendored byte-identical).
 *
 * A collapsible row: icon, title · subtitle, and a chevron that only appears when there
 * is a suffix to expand. The three inline SVGs are upstream's, transcribed attribute for
 * attribute — the stylesheet selects on their `data-slot` values.
 */

import type { ComponentPropsWithoutRef, MouseEvent, ReactNode } from "react";

import { Collapsible } from "./primitives/collapsible.tsx";

function BanIcon() {
  return (
    <svg
      data-slot="tool-error-card-ban"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M3.44283 12.5575L12.5495 3.45081M14.4446 8.00011C14.4446 11.5593 11.5593 14.4446 8.00011 14.4446C4.44094 14.4446 1.55566 11.5593 1.55566 8.00011C1.55566 4.44094 4.44094 1.55566 8.00011 1.55566C11.5593 1.55566 14.4446 4.44094 14.4446 8.00011Z"
        stroke="currentColor"
      />
    </svg>
  );
}

/** duo-progress-25: faint track ring + ~25% solid arc (Figma OpenCode DS) */
function LoaderIcon() {
  const r = 5.9;
  return (
    <svg
      data-slot="tool-error-card-loader"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <g transform="translate(8 8)">
        <circle
          r={r}
          fill="none"
          stroke="var(--v2-icon-icon-base)"
          strokeWidth="1"
          strokeOpacity="0.3"
          transform="rotate(-90)"
        />
        <circle
          r={r}
          fill="none"
          stroke="var(--v2-icon-icon-base)"
          strokeWidth="1"
          pathLength="100"
          strokeDasharray="25 75"
          transform="rotate(-90)"
        />
      </g>
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg
      data-slot="tool-error-card-chevron"
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M5.90795 9.62425C5.61628 9.81865 5.25 9.57825 5.25 9.19235V4.80837C5.25 4.42247 5.61628 4.18204 5.90795 4.37648L9.1959 6.56846C9.48535 6.7614 9.48535 7.2393 9.1959 7.43224L5.90795 9.62425Z"
        fill="currentColor"
      />
    </svg>
  );
}

export interface ToolErrorCardProps
  extends Omit<ComponentPropsWithoutRef<"div">, "children" | "title"> {
  title: ReactNode;
  subtitle: ReactNode;
  suffix?: ReactNode;
  loading?: boolean;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** When set, the subtitle renders as a link; clicking it does not toggle the row. */
  subtitleHref?: string;
}

function hasContent(node: ReactNode): boolean {
  if (node == null) return false;
  if (typeof node === "string") return node.length > 0;
  return true;
}

export function ToolErrorCard({
  title,
  subtitle,
  suffix,
  loading,
  open,
  defaultOpen,
  onOpenChange,
  subtitleHref,
  ...rest
}: ToolErrorCardProps) {
  const expandable = hasContent(suffix);
  const stop = (event: MouseEvent) => event.stopPropagation();

  return (
    <Collapsible
      {...rest}
      data-component="tool-error-card"
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      disabled={!expandable}
      aria-busy={loading ? true : undefined}
    >
      <Collapsible.Trigger asChild>
        <div role="button" data-slot="tool-error-card-trigger">
          <span data-slot="tool-error-card-icon-wrap">
            {loading ? <LoaderIcon /> : <BanIcon />}
          </span>
          <div data-slot="tool-error-card-main">
            <div data-slot="tool-error-card-labels">
              <span data-slot="tool-error-card-title">{title}</span>
              <span data-slot="tool-error-card-sep" aria-hidden="true">
                ·
              </span>
              {subtitleHref ? (
                <a
                  data-slot="tool-error-card-subtitle"
                  href={subtitleHref}
                  onClick={stop}
                  onPointerDown={stop}
                >
                  {subtitle}
                </a>
              ) : (
                <span data-slot="tool-error-card-subtitle">{subtitle}</span>
              )}
              {expandable ? (
                <span data-slot="tool-error-card-chevron-wrap">
                  <ChevronIcon />
                </span>
              ) : null}
            </div>
          </div>
        </div>
      </Collapsible.Trigger>
      {expandable ? (
        <Collapsible.Content data-slot="tool-error-card-content">
          <div data-slot="tool-error-card-suffix">{suffix}</div>
        </Collapsible.Content>
      ) : null}
    </Collapsible>
  );
}
